// Append-only record of everything sent to/from a venue plus arb engine decisions; the system of record.

import { journalAppend, journalAll, journalClear, journalPrune } from './db';

export type JournalKind =
  /** One wire round trip that could change state (POST/DELETE/PUT). */
  | 'wire'
  /** An engine-level placement: what the panel asked for and what came back. */
  | 'place'
  /** An engine-level cancel and its outcome. */
  | 'cancel'
  /** A fill the engine reported to the ladders. */
  | 'fill'
  /** An arb engine decision: quote, re-peg, refusal, hedge, cross, pull. */
  | 'arb'
  /** Money moved between exchange shards. */
  | 'transfer';

export interface JournalEntry {
  /** Monotonic within a page load; the IndexedDB key is separate. */
  seq: number;
  ts: number;
  venue: string;
  kind: JournalKind;
  /** Arb order id and leg (`arb-…/q`, `arb-…/h`) when the action was on its behalf. */
  tag?: string;
  orderId?: string;
  ticker?: string;
  /** Human summary, one line. */
  msg: string;
  /** Request and response as sent and received; JSON-serialisable. */
  data?: unknown;
  /** True when the venue or transport said no. */
  bad?: boolean;
}

/** Kept in memory for the Journal view; disk holds more. */
const MEM_MAX = 2000;
/** Disk retention: the newest this many entries survive a page load. */
const DISK_KEEP = 20_000;

const entries: JournalEntry[] = [];
const listeners = new Set<(e: JournalEntry) => void>();
let seq = 0;
let loaded = false;

function safeData(data: unknown): unknown {
  if (data === undefined) return undefined;
  try {
    // Round-trip so structured clone never meets a class instance or a cycle.
    return JSON.parse(JSON.stringify(data, (_k, v) => (typeof v === 'string' && v.length > 8000 ? v.slice(0, 8000) + '…' : v)));
  } catch {
    return String(data);
  }
}

export function journal(e: Omit<JournalEntry, 'seq' | 'ts'> & { ts?: number }): void {
  try {
    const entry: JournalEntry = { ...e, seq: ++seq, ts: e.ts ?? Date.now(), data: safeData(e.data) };
    entries.push(entry);
    if (entries.length > MEM_MAX) entries.shift();
    for (const fn of listeners) {
      try { fn(entry); } catch { /* a listener must not break the caller */ }
    }
    void journalAppend(entry).catch(() => undefined);
  } catch { /* never throw into an order path */ }
}

export function journalEntries(): readonly JournalEntry[] {
  return entries;
}

export function onJournal(fn: (e: JournalEntry) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Pull what earlier page loads wrote, so the view shows history at boot. */
export async function loadJournal(): Promise<void> {
  if (loaded) return;
  loaded = true;
  try {
    await journalPrune(DISK_KEEP);
    const old = await journalAll<JournalEntry>();
    const tail = old.slice(-MEM_MAX);
    // Older entries first; anything written since boot stays after them.
    entries.unshift(...tail.map((e) => ({ ...e, seq: 0 })));
    if (entries.length > MEM_MAX) entries.splice(0, entries.length - MEM_MAX);
  } catch { /* the view just starts empty */ }
}

/** Everything on disk as newline-delimited JSON, newest last. */
export async function exportJournal(): Promise<string> {
  const all = await journalAll<JournalEntry>();
  return all.map((e) => JSON.stringify(e)).join('\n') + '\n';
}

export async function clearJournal(): Promise<void> {
  entries.length = 0;
  await journalClear();
}
